import type { AuthenticatedComponentGuard } from "@src/auth/authenticated-component-guard";
import { createContext, useContext } from "react";
import type { UserStore } from "./schema";
import { UserNotAuthenticatedError } from "./user-error";

export type UserState = [UserStore | null, (user: Partial<UserStore>) => void];

export type UserProfileContextType = {
	/**
	 * User state and setter. Can be null if the user is not authenticated.
	 *
	 * @returns {UserState} The authenticated user and the function to set the user
	 */
	user: UserState;
	/**
	 * The color that represents the user in the UI.
	 *
	 * @returns {[string, (color: string) => void]} the color of the user's card and the function to set the color
	 */
	color: [string, (color: string) => void];
};

export const UserProfileContext = createContext<UserProfileContextType | null>(
	null,
);

export function useUserProfile() {
	const context = useContext(UserProfileContext);
	if (!context) {
		throw new Error("useUserProfile must be used within a UserProfileProvider");
	}
	return context;
}

/**
 * Returns the authenticated user. Can only be used within an authenticated component.
 * Use {@link AuthenticatedComponentGuard} to ensure the user is authenticated.
 *
 * @throws {UserNotAuthenticatedError} if the user is not authenticated
 * @returns {UserStore} the authenticated user
 */
export function useAuthenticatedUser(): UserStore {
	const { user } = useUserProfile();
	const [userState] = user;

	if (!userState) {
		throw new UserNotAuthenticatedError();
	}

	return userState;
}
